import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, Printer, Info } from "lucide-react";
import { fmtQty } from "@/lib/pack";

/**
 * Stock-value drill-down with three lenses (tabs) — chosen after the
 * "Reception holds $5k / Production $50k / 3DP $70k" snapshot question:
 *
 *  1. Primary owner (default): items where the selected dept is the
 *     primary. Sum matches the Reports card total exactly.
 *  2. Consumption-weighted: shared items split by last-90-day OUT usage
 *     per dept — fair when a dept only takes a small slice of a shared
 *     line (e.g. 3DP occasionally using Production's Clinell Wipes).
 *  3. All linked: every item the dept is linked to, full value plus
 *     shared-with tags. Sums can exceed the card total because a shared
 *     item counts for every dept it's linked to.
 *
 * Backed by GET /api/reports/stock-value-breakdown?department=X&days=90.
 */
const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const TABS = [
  {
    key: "primary",
    label: "Primary owner",
    hint: "Matches the card total",
  },
  {
    key: "weighted",
    label: "Consumption-weighted",
    hint: "Fair split by last 90d usage",
  },
  {
    key: "linked",
    label: "All linked",
    hint: "Every item this dept touches",
  },
];

const StockValueDetailDialog = ({ open, department, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("primary");

  useEffect(() => {
    if (!open) return;
    setTab("primary");
    setLoading(true);
    api
      .get("/reports/stock-value-breakdown", {
        params: { department: department || undefined, days: 90 },
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load breakdown"))
      .finally(() => setLoading(false));
  }, [open, department]);

  const mode = data?.modes?.[tab];
  const rows = mode?.items || [];
  const total = mode?.total ?? 0;
  const explainer = mode?.explainer || "";

  const scopeLabel =
    department && department !== "all"
      ? `${department} · on-hand value`
      : "All departments · on-hand value";

  const downloadCsv = () => {
    if (!rows.length) return;
    const headerBits = [
      "Item",
      "Type",
      "Primary dept",
      "Linked depts",
      "On-hand qty",
      "Unit",
      "Unit cost",
    ];
    if (tab === "weighted") headerBits.push("Share %", "Method");
    if (tab === "linked") headerBits.push("Shared with");
    headerBits.push("Line value");
    const csvRows = rows.map((r) => {
      const cells = [
        `"${(r.name || "").replace(/"/g, '""')}"`,
        r.type || "",
        r.primary_department || "",
        `"${(r.linked_departments || []).join(", ")}"`,
        fmtQty(r.qty),
        r.unit || "",
        Number(r.unit_cost || 0).toFixed(4),
      ];
      if (tab === "weighted")
        cells.push(
          `${Number(r.share_pct || 0).toFixed(1)}%`,
          r.method || "",
        );
      if (tab === "linked")
        cells.push(`"${(r.shared_with || []).join(", ")}"`);
      cells.push(Number(r.value || 0).toFixed(2));
      return cells.join(",");
    });
    const trailingBlanks = ",".repeat(headerBits.length - 2);
    const csv = [
      headerBits.join(","),
      ...csvRows,
      `${trailingBlanks},Total`,
      `${trailingBlanks},${total.toFixed(2)}`,
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dep = department && department !== "all" ? department : "all-depts";
    a.download = `stock-value-${dep}-${tab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:bg-white print:static print:inset-auto print:block print:z-auto"
      data-testid="stock-value-detail-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(1080px,96vw)] max-h-[92vh] flex flex-col overflow-hidden print:max-h-none print:h-auto print:w-full print:overflow-visible print:shadow-none print:border-0 print:rounded-none"
        onClick={(e) => e.stopPropagation()}
        data-print-target="stock-value"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between gap-3 flex-wrap print:hidden">
          <div>
            <div className="label-eyebrow">Reports · Stock value drill-down</div>
            <h3 className="font-display text-2xl">{scopeLabel}</h3>
            <div className="text-xs text-[#607087] mt-0.5">
              {rows.length} item{rows.length === 1 ? "" : "s"} · sorted by line value
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="stock-value-export-csv"
              onClick={downloadCsv}
              disabled={!rows.length}
              className="nb-btn nb-btn-dark px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-40"
            >
              <Download size={14} strokeWidth={2.5} /> CSV
            </button>
            <button
              data-testid="stock-value-export-pdf"
              onClick={() => window.print()}
              className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1.5"
              title="Print or Save as PDF"
            >
              <Printer size={14} strokeWidth={2.5} /> PDF
            </button>
            <button
              data-testid="stock-value-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
              aria-label="close"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block px-5 py-4 border-b-2 border-black">
          <div className="label-eyebrow">Podlab · Stock value</div>
          <h1 className="font-display text-3xl mt-1">
            {scopeLabel}{" "}
            <span className="text-lg text-[#607087]">
              · {TABS.find((t) => t.key === tab)?.label}
            </span>
          </h1>
          <div className="text-sm mt-1">
            <b>Total:</b> {money(total)} · {rows.length} item
            {rows.length === 1 ? "" : "s"} · <b>Generated:</b>{" "}
            {new Date().toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </div>
        </div>

        {/* Tabs */}
        <div
          className="px-5 pt-3 pb-2 border-b-2 border-black bg-white flex flex-wrap gap-1.5 print:hidden"
          data-testid="stock-value-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`stock-value-tab-${t.key}`}
              className={`nb-btn px-3 py-1.5 text-xs flex flex-col items-start ${
                tab === t.key ? "nb-btn-dark" : "bg-white"
              }`}
            >
              <span className="font-bold">{t.label}</span>
              <span className="opacity-70 text-[10px]">{t.hint}</span>
            </button>
          ))}
        </div>

        {/* Explainer + Total */}
        <div className="p-5 border-b-2 border-black bg-[#F1E7FA] flex items-start justify-between gap-3 flex-wrap print:bg-white">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-1.5 label-eyebrow">
              <Info size={12} strokeWidth={2.5} />
              How this lens works
            </div>
            <div className="text-xs text-[#5A2E7A] mt-1 max-w-[560px]">
              {explainer}
            </div>
          </div>
          <div>
            <div className="label-eyebrow">Total on-hand value</div>
            <div
              className="font-mono-num font-bold text-4xl"
              data-testid="stock-value-total"
            >
              {money(total)}
            </div>
          </div>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto scrollbar-clean print:overflow-visible">
          {loading && (
            <div className="p-10 text-center text-[#607087]">Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-[#607087] italic">
              No items with an on-hand value in this scope.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-white border-b-2 border-black sticky top-0">
                <tr className="text-left">
                  <th className="py-2 px-3 font-display">Item</th>
                  <th className="py-2 px-3 font-display">Primary</th>
                  {tab === "weighted" && (
                    <th className="py-2 px-3 font-display text-right">Share</th>
                  )}
                  {tab === "linked" && (
                    <th className="py-2 px-3 font-display">Shared with</th>
                  )}
                  <th className="py-2 px-3 font-display text-right">Qty</th>
                  <th className="py-2 px-3 font-display text-right">Unit cost</th>
                  <th className="py-2 px-3 font-display text-right">
                    {tab === "weighted" ? "Allocated" : "Line value"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#E0E5EA]"
                    data-testid={`stock-value-row-${r.id}`}
                  >
                    <td className="py-2 px-3 font-display truncate max-w-[280px]">
                      {r.name}
                    </td>
                    <td className="py-2 px-3 text-[#607087]">
                      {r.primary_department || "—"}
                    </td>
                    {tab === "weighted" && (
                      <td className="py-2 px-3 font-mono-num text-right">
                        {Number(r.share_pct || 0).toFixed(1)}%
                        {r.method === "primary_fallback" && (
                          <div className="text-[10px] text-[#B57A00]">
                            no 90d usage — fell back to primary
                          </div>
                        )}
                      </td>
                    )}
                    {tab === "linked" && (
                      <td className="py-2 px-3 text-[11px] text-[#607087] max-w-[220px]">
                        {(r.shared_with || []).length > 0
                          ? (r.shared_with || []).join(", ")
                          : "—"}
                      </td>
                    )}
                    <td className="py-2 px-3 font-mono-num text-right">
                      {fmtQty(r.qty)} <span className="text-[#607087]">{r.unit}</span>
                    </td>
                    <td className="py-2 px-3 font-mono-num text-right text-[#607087]">
                      {money(r.unit_cost)}
                    </td>
                    <td className="py-2 px-3 font-mono-num text-right font-bold">
                      {money(r.value)}
                      {tab === "weighted" && r.value !== r.line_value && (
                        <div className="text-[10px] text-[#607087] font-normal">
                          of {money(r.line_value)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockValueDetailDialog;
